<template>
  <v-card
    v-if="displayPaymentDetails"
    border
    tile
  >
    <v-card-title>
      <h2 class="fs-20 fw-800">
        {{ $bct('Financial programs') }}
      </h2>
    </v-card-title>

    <v-card-text>
      <v-row v-if="provider.accepts_subsidy">
        <div class="mb-2">
          <v-icon
            class="me-2 ms-2 fs-16"
            color="#00A391"
            icon="check_circle_outline"
            size="24"
          />
          <span class="fs-16 fw-400">
            {{ $bct('This child care provider accepts financial aid') }}
          </span>
        </div>
      </v-row>

      <v-row
        v-if="provider.payment_subsidies_accepted.length > 0"
        class="mb-2 fs-16"
        dense
      >
        <v-col cols="1">
          <v-icon
            class="fs-24 mt-0"
            color="#578052"
            icon="attach_money"
          />
        </v-col>

        <v-col cols="11">
          <div class="mb-1 fs-16 fw-500">
            {{ $bct(getLabelByProperty('payment_subsidies_accepted', 'Subsidies accepted')) }}
          </div>

          <div
            v-for="(subsidy, index) in provider.payment_subsidies_accepted"
            v-text="subsidy"
            :key="index"
            class="fs-16 fw-400"
          />
        </v-col>
      </v-row>

      <v-row
        v-if="provider.payment_other_programs.length > 0"
        class="mb-2"
        dense
      >
        <v-col cols="1">
          <img
            :src="$a.url('icons.headstart')"
            alt="Head Start icon"
            class="h-24"
          />
        </v-col>

        <v-col cols="11">
          <div class="mb-1 fs-16 fw-500">
            {{ $bct(getLabelByProperty('payment_other_programs', 'Other assistance programs')) }}
          </div>

          <div
            v-for="(program, index) in provider.payment_other_programs"
            v-text="$bct(program)"
            :key="index"
            class="fs-16 fw-400"
          />
        </v-col>
      </v-row>

      <v-row
        v-if="provider.payment_other_description"
        class="mb-4"
        dense
      >
        <v-col cols="1">
          <v-icon
            class="fs-24"
            color="primary"
            icon="loyalty"
          />
        </v-col>

        <v-col cols="11">
          <div class="fs-16 fw-500 mb-1">
            {{
              $bct(getLabelByProperty('payment_other_description', 'Additional payment options'))
            }}
          </div>

          <div class="fs-16 fw-400">
            {{ $bct(provider.payment_other_description) }}
          </div>
        </v-col>
      </v-row>

      <v-row
        v-if="provider.payment_methods_accepted.length > 0"
        dense
      >
        <v-col cols="12">
          <div class="fs-16 fw-500 mb-2">
            {{ $bct(getLabelByProperty('payment_methods_accepted', 'Payment methods accepted')) }}
          </div>

          <div class="d-flex ga-3">
            <div
              v-for="(method, index) in provider.payment_methods_accepted"
              :key="index"
              class="d-flex ga-1"
            >
              <v-icon
                class="va-text-center"
                color="primary"
                icon="check_circle"
                size="24"
              />

              <span class="fs-16 fw-400">
                {{ translatePaymentMethod(method) }}
              </span>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
import useSchematizedField from '@/shared/composables/useSchematizedField';

const UNTRANSLATED_PAYMENT_METHODS = ['American Express', 'Mastercard', 'Visa'];

const props = defineProps({
  provider: {
    type: Object,
    default: null,
  },
});

const { getLabelByProperty } = useSchematizedField(
  ref(null),
  ref(null),
  ref(null),
  ref(null),
  computed(() => props.provider?.schema_id),
);

const $bct = inject('translator');

const displayPaymentDetails = computed(() => {
  return (
    props.provider.accepts_subsidy ||
    props.provider.payment_subsidies_accepted.length > 0 ||
    props.provider.payment_other_programs.length > 0 ||
    props.provider.payment_other_description ||
    props.provider.payment_methods_accepted.length > 0
  );
});

function translatePaymentMethod(method) {
  return UNTRANSLATED_PAYMENT_METHODS.includes(method) ? method : $bct(method);
}
</script>
